import React, { createContext, useContext, useState, ReactNode } from 'react';
import SacredTheme, { SACRED_PALETTES, SACRED_COLORS, SACRED_GRADIENTS } from '../utils/sacredTheme';

// ============================================================================
// TYPES
// ============================================================================

type PaletteName = keyof typeof SACRED_PALETTES;

interface ThemeContextType {
  theme: typeof SacredTheme;
  currentPalette: PaletteName;
  setPalette: (palette: PaletteName) => void;
  colors: typeof SACRED_COLORS;
  gradients: typeof SACRED_GRADIENTS;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentPalette, setCurrentPalette] = useState<PaletteName>('fr00tEssence');

  const value: ThemeContextType = {
    theme: SacredTheme,
    currentPalette,
    setPalette: setCurrentPalette,
    colors: SACRED_COLORS,
    gradients: SACRED_GRADIENTS,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access the theme throughout the application
 * 
 * Usage:
 * ```tsx
 * const { theme, colors, gradients, currentPalette, setPalette } = useTheme();
 * ```
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
